// Очередь загрузок редактора (п.24): задача живёт ВНЕ компонента, поэтому уход
// в другой блок больше не убивает догрузку картинки. Дефолт без провайдера —
// «выполнить на месте, ровно как раньше»: `ImageUpload` стоит и вне редактора
// (категории, статьи), где никакой очереди нет.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useStore } from 'react-redux'
import type { RootState } from '@/store'
import { resolveAlias } from '@/features/editor/lib/idAliases'
import { useFileUpload } from './useFileUpload'
import {
  placeKey,
  runUploadTask,
  type BlockUploadTarget,
  type CommitUpload,
  type UploadTask,
} from './uploadTask'
import { useBlockUploadCommit } from './useBlockUploadCommit'
import { useFrozenUploadGuard } from './useFrozenUploadGuard'

export interface UploadQueue {
  enqueue: (task: UploadTask) => void
  /** Сколько задач ещё не закончено — счётчик, а не список: очередь несёт
   *  файлы, показывать их содержимое некому. */
  pending: number
}

const UploadQueueContext = createContext<UploadQueue | null>(null)

/**
 * Общий движок обеих реализаций. Задачи идут ПОСЛЕДОВАТЕЛЬНО: две картинки,
 * брошенные подряд, не соревнуются за канал. Куда уйдёт URL, решает
 * `resolveCommit` — но само решение принимается уже В МОМЕНТ коммита, потому
 * что за время загрузки автор успевает уйти.
 */
const useQueueRunner = (resolveCommit: (task: UploadTask) => CommitUpload): UploadQueue => {
  const runner = useFileUpload()
  const ref = useRef({ runner, resolveCommit })
  useEffect(() => {
    ref.current = { runner, resolveCommit }
  })
  const [pending, setPending] = useState(0)
  const chain = useRef<Promise<void>>(Promise.resolve())

  const enqueue = useCallback((task: UploadTask) => {
    const commit = ref.current.resolveCommit(task)
    setPending((n) => n + 1)
    chain.current = chain.current
      .then(() => runUploadTask(task, ref.current.runner, commit))
      .catch(() => {})
      .finally(() => setPending((n) => n - 1))
  }, [])

  return useMemo(() => ({ enqueue, pending }), [enqueue, pending])
}

interface Props {
  children: ReactNode
  courseId: number
}

export const UploadQueueProvider = ({ children, courseId }: Props) => {
  const buildBlockCommit = useBlockUploadCommit(courseId)
  const guardFrozen = useFrozenUploadGuard(courseId)
  const store = useStore<RootState>()

  // Пока место загрузки на экране, коммит остаётся ЕГО: URL уходит в живой
  // черновик, а запись — за кнопкой «Сохранить», как у любой другой правки
  // блока. Фоновая запись из свежего серверного блока включается только тогда,
  // ради чего и заведена, — когда потребителя больше нет. Решение принимается
  // В МОМЕНТ КОММИТА: за время загрузки автор как раз и уходит.
  const resolveCommit = useCallback(
    (task: UploadTask): CommitUpload => {
      const target = task.block
      // Не блок — значит курс или урок (баннер, картинка урока): их запись
      // проверка отбивает так же, как запись блока.
      if (!target) return guardFrozen(task.commit)
      const background = buildBlockCommit(target)
      return (url) => {
        // Место сверяется через алиасы: сохранение живого блока вернуло блок
        // черновика (11 → 51), а панель осталась на ТОМ ЖЕ месте — URL обязан уйти
        // в её черновик, а не в фоновый `PUT` (он бы отказал или взял отставшее тело).
        const aliases = store.getState().editor.idAliases
        const keyOf = (t: BlockUploadTarget) =>
          placeKey({ ...t, blockId: resolveAlias(aliases, 'block', t.blockId) })
        if (!task.isAlive?.(keyOf)) return background(url)
        task.commit(url)
        // URL ушёл в ЧЕРНОВИК — подтверждённого сохранения не было, и старый
        // файл трогать нельзя: серверный блок ссылается на него до нажатия
        // «Сохранить», а если автор не сохранит — навсегда.
        return false
      }
    },
    [buildBlockCommit, guardFrozen, store],
  )

  const value = useQueueRunner(resolveCommit)
  return <UploadQueueContext.Provider value={value}>{children}</UploadQueueContext.Provider>
}

/** Дефолт без провайдера — сегодняшнее поведение: грузим на месте и отдаём URL
 *  в `commit` (`onChange` вызывающего). Без него сломались бы категории и
 *  статьи, живущие вне редактора. */
// eslint-disable-next-line react-refresh/only-export-components
export const useUploadQueue = (): UploadQueue => {
  const shared = useContext(UploadQueueContext)
  const local = useQueueRunner(useCallback((task: UploadTask) => task.commit, []))
  return shared ?? local
}
